import { ApolloError, gql } from "@apollo/client";
import {
  Button,
  Container,
  Typography,
} from "@mui/material";
import { PropsWithChildren, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useOrganizationProviderConfigQuery, useUserDetailQuery } from "~/graphql/__generated__/graphql";
import UserSearchBox from "~/components/users/UserSearchBox";
import { useSnackbar } from "~/hooks/useSnackbar";
import UserDetailScreen from "~/components/users/UserDetailsScreen";
import {
  ProviderDetails,
  DEFAULT_PROVIDER_DETAILS,
  DEFAULT_USER_DETAILS,
} from "~/components/users/userTypes";
import { providerTraits } from "@caredna-marlin/utils/providerTraits";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

const Heading = ({ children }: PropsWithChildren) => <Typography variant="h6" sx={{ mb: 2 }}>{children}</Typography>

interface Props {
  id?: number;
}

const firstOrEmpty = (values: string[]) => values?.length ? values[0] : "";
const traitsOfType = (provider: {traits: {trait: string, value: string}[]}, type: string): string[] => {
  return provider.traits.filter(t => t.trait == type).map(t => t.value);
}

export default function TabManage({ id }: Props) {
  const { setErrorMessage } = useSnackbar();
  const navigate = useNavigate();

  const { data: userData, loading: userLoading, error: userError } = useUserDetailQuery({
    variables: { userId: id ?? 0 },
    skip: !id,
  });

  const userDetails = useMemo(() => {
    const orgUser = userData?.userDetails;
    if (!orgUser?.user) {
      return DEFAULT_USER_DETAILS
    }

    const provider = orgUser.providers?.length ? orgUser.providers[0] : undefined;

    const providerDetails: ProviderDetails = provider ? {
      id: provider.id,
      active: provider.active,
      addressCity: provider.addressCity,
      addressLineTwo: provider.addressLineTwo,
      addressState: provider.addressState,
      addressStreet: provider.addressStreet,
      addressZip: provider.addressZip,
      addressPlaceId: null,
      providerTypeId: provider.providerTypeId,
      teams: provider.teamProviders.map(t => t.teamId),
      gender: firstOrEmpty(traitsOfType(provider, "gender")),
      languages: traitsOfType(provider, "language"),
      licensedStates: traitsOfType(provider, "stateLicense"),
      external: provider.external,
      identifier: provider.identifier,
    } : DEFAULT_PROVIDER_DETAILS;

    return {
      email: orgUser.user.email,
      firstName: orgUser.user.firstName,
      lastName: orgUser.user.lastName,
      deleted: orgUser.deleted,
      roles: orgUser.roles.filter(r => r.userRoleId).map(r => r.userRoleId!),
      provider: providerDetails,
      userIsProvider: !!(providerDetails.id && orgUser.deleted) || (providerDetails.active),
      organizationalUnits: orgUser.organizationalUnitOrgUsers.map((membership) => membership.organizationalUnitId),
      serviceLines: orgUser.serviceLineOrgUsers.map(sl => sl.serviceLineId),
    }

  }, [userData])

  const { data: orgDetails, loading: orgDetailsLoading, error: orgDetailsError } = useOrganizationProviderConfigQuery();

  const loading = useMemo(() => userLoading || orgDetailsLoading, [orgDetailsLoading, userLoading]);
  const error = useMemo(() => userError || orgDetailsError, [orgDetailsError, userError]);

  const onError = (errorDetail: ApolloError | string) => {
    console.error(errorDetail);
    setErrorMessage("Error fetching user details")
    navigate("/users/manage")
  }

  const options = useMemo(() => {
    const organization = orgDetails?.organizationSingle
    if (!organization) { return null }

    return {
      roles: organization.roles,
      states: [...providerTraits.stateLicense],
      genders: [...providerTraits.gender, {label: "Not on file", value: ""}],
      languages: [...providerTraits.language],
      providerTypes: organization.providerTypes,
      organizationalUnits: organization.organizationalUnits,
      licensableProviderTypes: organization.providerTypes.filter(pt => pt.requireStateLicense).map(pt => pt.name),
      serviceLines: organization.serviceLines,
      teams: organization.teams,
    }
  }, [orgDetails])

  useEffect(() => {
    if(orgDetailsError) {
      onError(orgDetailsError)
    }
  }, [orgDetailsError])

  const heading = useMemo(() => `${userDetails?.firstName && !userLoading ? `Editing User: ${userDetails.firstName} ${userDetails.lastName} (${userDetails.email})` : "Add/Edit User"}`, [userLoading, userDetails]);
  const user = useMemo(() => ({id, details: userDetails}), [id, userDetails]);

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      <Heading>{heading}</Heading>
      {(id === undefined || error) && (<>
        <UserSearchBox />
        <Button
          onClick={() => navigate("/users/manage/0")}
          size="small"
          startIcon={<AddCircleOutlineIcon fontSize="small" />}
        >
          Add new user
        </Button>
      </>)}
      {(id !== undefined && options) && (<>
        <Button
          sx={{mt:-3}}
          onClick={() => navigate(-1)}
          size="small"
          startIcon={<ArrowBackIcon fontSize="small" />}
        >
          Back
        </Button>
        <UserDetailScreen key={user.id} loading={loading} user={user} options={options} />
      </>)}
    </Container>
  );
}

gql`
  query UserDetail($userId: Int)
  @permission(requires: manageUsers)
  {
    userDetails(id: $userId) {
      id
      deleted

      user {
        id
        email
        firstName
        lastName
      }

      roles {
        userRoleId
      }

      serviceLineOrgUsers {
        serviceLineId
      }

      organizationalUnitOrgUsers {
        organizationalUnitId
      }

      providers {
        id
        active
        providerTypeId
        addressStreet
        addressLineTwo
        addressCity
        addressState
        addressZip
        external
        identifier

        teamProviders {
          teamId
        }

        traits {
          trait
          value
        }
      }
    }
  }
`

gql`
  query OrganizationProviderConfig
  @permission(requires: manageUsers)
  {
    organizationSingle {
      teams {
        id
        name
      }

      serviceLines {
        id
        name
      }

      organizationalUnits {
        id
        name
        active
      }

      providerTypes {
        id
        name
        requireStateLicense
      }

      roles {
        id
        name
      }
    }
  }
`
